import { memo, type ReactNode } from 'react';
import { Flex, Text } from '@chakra-ui/react';

import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';

import {
  PLAN_CARD_TOOLBAR_PY,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TABLE_TOOLBAR_BG,
  PLAN_TEXT,
  controlHeight,
  font,
  fontSize,
  neutral,
  text as textToken,
} from '../planPageRevampUi';
import InfoIcon from '../icons/InfoIcon';

import PlanTimeFilterSegment, { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  hint: string;
  /** Hidden entirely when the model's own granularity is already 'Y' — same gate the table used. */
  showTimeToggle?: boolean;
  timeValue?: PlanTimeFilterValue;
  onTimeChange?: (value: PlanTimeFilterValue) => void;
  onDownload: () => void;
  isExporting?: boolean;
  /** Extra controls between the leading slot and the time toggle — variance %, etc. */
  leadingActions?: ReactNode;
  /** Rendered right after the hint text, on the left — the chart-type toggle. */
  hintTrailing?: ReactNode;
}

/**
 * The toolbar strip shared by the chart card and the table card (Figma
 * 76685:6567 / 76685:5631): hint text left, M/Q/Y + Download right. One
 * component renders both, where before each card hand-rolled its own
 * version of the same row and they'd drifted (the chart toolbar had no
 * hint, a different Download button, and ran on `themeColors` instead of
 * the shared tokens).
 */
const PlanCardToolbarStrip = ({
  hint,
  showTimeToggle = true,
  timeValue,
  onTimeChange,
  onDownload,
  isExporting = false,
  leadingActions,
  hintTrailing,
}: Properties) => {
  return (
    <Flex
      alignItems='center'
      justifyContent='space-between'
      flexShrink={0}
      flexWrap='wrap'
      overflow='hidden'
      borderBottom='1px solid'
      borderColor={neutral.border}
      bg={PLAN_TABLE_TOOLBAR_BG}
      px={4}
      py={PLAN_CARD_TOOLBAR_PY}
      gap={3}
      w='100%'
      maxW='100%'
    >
      <Flex alignItems='center' gap={3} minW={0} flex='1'>
        <Flex alignItems='center' gap={2} minW={0}>
          <InfoIcon width={12} height={12} color={textToken.secondary} />
          <Text {...PLAN_TEXT.labelSm} color={textToken.secondary} noOfLines={1}>
            {hint}
          </Text>
        </Flex>
        {hintTrailing}
      </Flex>

      <Flex alignItems='center' gap={3} flexShrink={0}>
        {leadingActions}

        {showTimeToggle && timeValue && onTimeChange && (
          <PlanTimeFilterSegment value={timeValue} onChange={onTimeChange} />
        )}

        {/* The Dimensions table's own Download control, lifted byte-for-byte
            from `DimensionsRevamp/NewTable.tsx` — a plain `Flex as='button'`,
            not a Chakra `Button`. The `Button` component was carrying its own
            default font size/weight, box-shadow and disabled/loading chrome
            that Dimensions' control doesn't have, which is what made the two
            read as different controls despite sharing the same tokens. */}
        <Flex
          as='button'
          type='button'
          alignItems='center'
          gap='8px'
          bg={neutral.surface}
          border={`1px solid ${neutral.border}`}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          px='12px'
          height={controlHeight}
          cursor={isExporting ? 'default' : 'pointer'}
          opacity={isExporting ? 0.6 : 1}
          pointerEvents={isExporting ? 'none' : 'auto'}
          flexShrink={0}
          transition='background 0.12s ease, border-color 0.12s ease'
          _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
          onClick={onDownload}
        >
          <FigmaFileDownloadIcon width={14} height={14} color={textToken.secondary} />
          <Text
            fontSize={fontSize.body}
            fontWeight='500'
            fontFamily={font.body}
            color={textToken.primary}
            whiteSpace='nowrap'
          >
            {isExporting ? 'Downloading' : 'Download'}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default memo(PlanCardToolbarStrip);
